import { Btn, Bx, IconBtn, Input, Typo } from '@curry-group/mui-curcuma';
import { Grid } from '@material-ui/core';
import { faCheck, faFlaskPotion, faSearch, faRedo, faTools, faEnvelope } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CommunityAdminStatus, fetchDeactivatedCommunities, IDeactivatedCommunity, reactivateCommunity, recheckInactiveCommunities, repairMgmtParticipations, resendWarningEmail } from '../../data/reducer/userprofile';
import { IListItem } from '../../model/list/ListItem';
import { ThingTypes } from '../../model/ryve/Thing';
import { NotFoundComponent } from '../detail/not-found';
import { ListWidget } from '../widget/list';

type SortKey = 'date' | 'alpha';

const toListItem = (c: IDeactivatedCommunity, status: CommunityAdminStatus): IListItem => ({
  _id: c.id,
  __original: c,
  typeId: c.type,
  alias: c.alias,
  title: c.title || '(unbenannt)',
  type: c.type === ThingTypes.Project ? 'Projekt' : 'Arbeitsgruppe',
  color: c.type === ThingTypes.Project ? 'secondary' : 'primary',
  avatar: { type: 'icon', icon: faFlaskPotion },
  timestamp: c.activityDate ? new Date(c.activityDate).toISOString() : undefined,
  subtitle: status === 'grace' && c.deactivationDeadline
    ? `Deaktivierung am ${new Date(c.deactivationDeadline).toLocaleDateString('de-DE')}`
    : undefined,
  isPrivate: !c.public,
  isHidden: !c.listed,
  isDeactivated: status === 'deactivated',
});

export const DeactivatedCommunitiesAdminComponent: React.FC = () => {
  const dispatch = useDispatch();
  const isAdmin = useSelector(state => state.foundation?.profile?.isAdmin);
  const items = useSelector(state => state.userProfile?.deactivatedCommunities) || [];
  const working = useSelector(state => state.userProfile?.deactivatedCommunitiesWorking);
  const recheckWorking = useSelector(state => state.userProfile?.recheckInactiveCommunitiesWorking);
  const repairWorking = useSelector(state => state.userProfile?.repairMgmtParticipationsWorking);
  const repairResult = useSelector(state => state.userProfile?.repairMgmtParticipationsResult);
  const resendWorkingIds = useSelector(state => state.userProfile?.resendWarningWorkingIds) || [];
  const resendResult = useSelector(state => state.userProfile?.resendWarningResult);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortKey>('date');
  const [status, setStatus] = useState<CommunityAdminStatus>('deactivated');

  useEffect(() => {
    if (!isAdmin) return;
    dispatch(fetchDeactivatedCommunities({ q: query, sort, skip: 0, take: 100, status }));
  }, [dispatch, isAdmin, sort, status]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAdmin) {
    return <NotFoundComponent />;
  }

  const isGrace = status === 'grace';

  const onSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(fetchDeactivatedCommunities({ q: query, sort, skip: 0, take: 100, status }));
  };

  const listItems: IListItem[] = items.map(c => toListItem(c, status));

  return (
    <Bx pb={4}>
      <Bx pb={2} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={8}>
        <Typo variant={'h3'} component={'h3'}>
          {isGrace ? 'Communities in Kulanzfrist' : 'Deaktivierte Communities'}
        </Typo>
        <Bx display="flex" gridGap={8} flexWrap="wrap">
          <Btn
            size="medium"
            variant="outlined"
            color="primary"
            disabled={!!recheckWorking}
            startIcon={<FontAwesomeIcon icon={faRedo} />}
            onClick={() => dispatch(recheckInactiveCommunities({}))}
            title="Inaktivitätsprüfung sofort ausführen"
          >
            {recheckWorking ? 'Recheck läuft…' : 'Recheck inaktive Communities'}
          </Btn>
          <Btn
            size="medium"
            variant="outlined"
            color="default"
            disabled={!!repairWorking}
            startIcon={<FontAwesomeIcon icon={faTools} />}
            onClick={() => dispatch(repairMgmtParticipations({}))}
            title="Fügt für UWAO_MAIL_ADRESSES_USER_MGMT-User die Admin-Rolle und MmoaoParticipation in Communities hinzu, in denen sie als Teilnehmer existieren."
          >
            {repairWorking ? 'Repariere…' : 'Mgmt-Admin Teilnahmen reparieren'}
          </Btn>
        </Bx>
      </Bx>
      <Bx pb={2} display="flex" gridGap={8} flexWrap="wrap">
        <Btn
          size="small"
          variant={status === 'deactivated' ? 'contained' : 'outlined'}
          color={status === 'deactivated' ? 'primary' : 'default'}
          onClick={() => setStatus('deactivated')}
        >
          Deaktiviert
        </Btn>
        <Btn
          size="small"
          variant={status === 'grace' ? 'contained' : 'outlined'}
          color={status === 'grace' ? 'primary' : 'default'}
          onClick={() => setStatus('grace')}
        >
          In Kulanzfrist
        </Btn>
      </Bx>
      {!!repairResult && !repairWorking && (
        <Bx pb={2}>
          <Typo variant="body2" color="textSecondary">
            Repair fertig: {repairResult.scanned ?? 0} Communities geprüft,
            {' '}{repairResult.fixedParticipants ?? 0} Teilnehmer-Einträge angepasst,
            {' '}{repairResult.fixedParticipations ?? 0} fehlende Participations erstellt.
          </Typo>
        </Bx>
      )}
      {!!resendResult && (
        <Bx pb={2}>
          <Typo variant="body2" color="textSecondary">
            {resendResult.recipients > 0
              ? `Warn-E-Mail an ${resendResult.recipients} Admin(s) gesendet.`
              : 'Keine Admin-Empfänger gefunden — es wurde keine E-Mail gesendet.'}
          </Typo>
        </Bx>
      )}
      <form onSubmit={onSearchSubmit}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={6}>
            <Input
              inputLabel={{ children: 'Suche (Titel)' }}
              input={{
                value: query,
                fullWidth: true,
                onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setQuery(e.target.value),
                placeholder: 'Titel filtern…',
              }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Btn
              type="submit"
              size="large"
              fullWidth
              variant="outlined"
              startIcon={<FontAwesomeIcon icon={faSearch} />}
            >
              Suchen
            </Btn>
          </Grid>
          <Grid item xs={6} md={3}>
            <Btn
              size="large"
              fullWidth
              variant={sort === 'date' ? 'contained' : 'outlined'}
              color={sort === 'date' ? 'primary' : 'default'}
              onClick={() => setSort('date')}
              title={isGrace ? 'Nach Deaktivierungsfrist (bald zuerst)' : 'Nach Aktivitätsdatum'}
            >
              {isGrace ? 'Frist' : 'Datum'}
            </Btn>
          </Grid>
        </Grid>
        <Bx mt={1} display="flex" justifyContent="flex-end">
          <Btn
            size="small"
            variant={sort === 'alpha' ? 'contained' : 'outlined'}
            color={sort === 'alpha' ? 'primary' : 'default'}
            onClick={() => setSort('alpha')}
          >
            Alphabetisch sortieren
          </Btn>
        </Bx>
      </form>

      <Bx mt={2} maxHeight={480} style={{ overflowY: 'auto' }}>
        {listItems.length > 0 ? (
          <ListWidget
            working={working}
            items={listItems}
            headline={isGrace
              ? `${listItems.length} Communit${listItems.length === 1 ? 'y' : 'ies'} in Kulanzfrist`
              : `${listItems.length} deaktivierte Communit${listItems.length === 1 ? 'y' : 'ies'}`}
            itemActions={item => [
              <Bx
                key={'btnActions'}
                display="flex"
                flexGrow={1}
                justifyContent="flex-end"
                alignItems="center"
                gridGap={8}
              >
                {isGrace && (
                  <IconBtn
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      dispatch(resendWarningEmail({ id: item._id }));
                    }}
                    disabled={resendWorkingIds.includes(item._id)}
                    color="primary"
                    size="medium"
                    variant="outlined"
                    title="Warn-E-Mail erneut senden"
                  >
                    <FontAwesomeIcon icon={faEnvelope} />
                  </IconBtn>
                )}
                <IconBtn
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    dispatch(reactivateCommunity({ id: item._id }));
                  }}
                  color="default"
                  size="medium"
                  variant="outlined"
                  title={isGrace ? 'Inaktivität zurücksetzen' : 'Aktivieren'}
                >
                  <FontAwesomeIcon icon={faCheck} />
                </IconBtn>
              </Bx>,
            ]}
          />
        ) : (
          <ListWidget ignoreWorking={!working} items={undefined} headline={working ? 'Lade…' : (isGrace ? 'Keine Communities in Kulanzfrist' : 'Keine deaktivierten Communities')} />
        )}
      </Bx>
    </Bx>
  );
};
